import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { BulkEditPreviewListingDetails } from 'src/components/common/BulkActions/BulkEditStatus/components/BulkEditPreviewDisplay/BulkEditPreviewListingDetails';
import {
  AttachmentDisplay,
  AttachmentInfo,
} from 'src/components/Messages/AttachmentsPanel';
import { validateAttachments } from 'src/components/ReachProSupport/attachmentValidation';
import { SectionSelector } from 'src/components/Selectors/SectionRowSelector';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContextDataSelector } from 'src/contexts/CatalogDataContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useGetEventMapConfig } from 'src/contexts/EventMapContext';
import { useReportedMissingSections } from 'src/contexts/ReportedMissingSectionsContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { PosTextField } from 'src/core/POS/PosTextField';
import { shared, vars } from 'src/core/themes';
import { Button, Popover, Stack, Tooltip } from 'src/core/ui';
import { HoverableIcon } from 'src/core/ui/HoverableIcon/HoverableIcon';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AddFileIcon } from 'src/svgs/AddFileIcon';
import {
  ClockIcon,
  IconsFill,
  LoadingIcon,
  WarningOutlineIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  Feature,
  Marketplace,
  Permission,
} from 'src/WebApiController';

import { SECTION_MAPPING_BULK_UPDATE_KEY } from './SectionMappingControl.constants';
import * as styles from './SectionMappingControl.css';
import {
  useApplySectionMapping,
  useEventProofUrl,
  useReportMissingSection,
  useSectionMappingBulk,
} from './SectionMappingControl.hooks';
import { SectionMappingControlProps } from './SectionMappingControl.types';

const toAttachmentInfo = (f: File): AttachmentInfo => ({
  attachmentId: f.name,
  name: f.name,
  size: f.size,
});

/**
 * The non-interactive unmapped-section indicator. Rendered as-is when
 * self-service section mapping is unavailable (feature off, no permission, or
 * no mapped event), preserving the pre-existing behavior.
 */
const UnmappedSectionWarning = () => (
  <HoverableIcon
    icon={<WarningOutlineIcon fill={'#FD9927'} size={vars.iconSize.m} />}
    tooltip={<Content id={ContentId.SeatingInfoNotMatchStubHub} />}
  />
);

/**
 * The "requested" indicator (POS-9306 FR2/FR7). Once a missing-section report
 * has been submitted for the listing, the red unmapped trigger is replaced by a
 * distinct clock icon so the seller can tell a pending request apart from a
 * section that still needs action.
 */
const RequestedSectionIndicator = () => (
  <HoverableIcon
    icon={
      <ClockIcon fill={vars.color.textBrand} size={vars.iconSize.m} noOutline />
    }
    tooltip={<Content id={ContentId.SectionRequestPending} />}
  />
);

/**
 * Self-service section mapping trigger for an unmapped listing (POS-9305).
 * Surfaces a red exclamation trigger that opens a popover with a StubHub
 * section dropdown; selecting a section and applying maps the listing's
 * StubHub marketplace seating override to it. When the section is missing
 * entirely, the popover also offers reporting it to the catalog team (POS-9306).
 */
export const SectionMappingControl = ({
  listing,
}: SectionMappingControlProps) => {
  const { isReported } = useReportedMissingSections();
  const catalogData = useCatalogDataContextDataSelector();
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_UpdateListingInfo,
    Permission.Inventory_UpdateListingInfoOnIsPricerOf,
    Permission.Inventory_UpdateListingInfoOnPurchasedBy
  );

  // The listing carries no venue config id; resolve it from the catalog event so
  // the per-section "requested" state can be keyed on (venue config, section).
  const venueConfigId =
    listing.viagEvId != null
      ? (catalogData?.events?.[listing.viagEvId.toString()]?.event?.venCfgId ??
        null)
      : null;

  // A listing is "requested" only when its own section has a pending request
  // (no fallback to a config/event-level report). Check both the raw seller
  // section and the StubHub section override, since the override is the section
  // the listing is actually mapped to.
  const stubhubOverrideSection = listing.mkpListings?.find(
    (m) => m.mkp === Marketplace.StubHub
  )?.overrideSection;
  const reported =
    isReported(venueConfigId, listing.seating.section) ||
    (stubhubOverrideSection != null &&
      isReported(venueConfigId, stubhubOverrideSection));

  // When mapping isn't available (feature off / no permission / unmapped event),
  // fall back to a static indicator — the clock if a report is pending, else the
  // amber warning.
  if (!hasFeature || !hasPermission || listing.viagEvId == null) {
    return reported ? (
      <RequestedSectionIndicator />
    ) : (
      <UnmappedSectionWarning />
    );
  }

  // Even once a missing-section report is pending, keep the mapper interactive so
  // the seller can still override the section to an existing StubHub section.
  return (
    <SectionMapper
      listing={listing}
      venueConfigId={venueConfigId}
      isReported={reported}
    />
  );
};

const SectionMapper = ({
  listing,
  venueConfigId,
  isReported,
}: SectionMappingControlProps & {
  venueConfigId: number | null;
  isReported: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const [applyToAll, setApplyToAll] = useState(false);
  // POS-9305 FR4.1: persist the mapping as a VenueConfig + Section override so
  // future inventory for the same config/section is auto-mapped.
  const [persistOverride, setPersistOverride] = useState(false);

  // Keep the venue map / bulk preview loaded while either surface is active.
  const isActive = open || previewOpen;
  const venueMapQuery = useGetEventMapConfig(listing.viagEvId, !isActive);
  const sections = venueMapQuery.data?.sections;
  const hasNoSections =
    venueMapQuery.isSuccess && (sections?.length ?? 0) === 0;
  const selectedSection = sections?.find(
    (s) => s.id.toString() === selectedSectionId
  );

  const { applyMapping, isApplying } = useApplySectionMapping(listing);
  const bulk = useSectionMappingBulk(
    listing,
    venueMapQuery.data?.venueCfgId,
    isActive
  );
  const { reportMissingSection, isReporting } = useReportMissingSection(
    listing,
    venueConfigId
  );
  const [reportMode, setReportMode] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportFiles, setReportFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string>();
  const [reportUrl, setReportUrl] = useState('');
  const [urlDirty, setUrlDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerLabel = useContent(ContentId.ChooseSection);
  const reportPlaceholder = useContent(
    ContentId.ReportMissingSectionPlaceholder
  );
  const proofUrlPlaceholder = useContent(ContentId.ReportProofUrlPlaceholder);

  // FR6: prefill the proof URL from the event's mapping, only once the seller
  // enters the report flow (not merely opening the mapping popover), and unless
  // they have already edited the field.
  const { proofUrl } = useEventProofUrl(listing.viagEvId, reportMode);
  useEffect(() => {
    if (!urlDirty && proofUrl) {
      setReportUrl(proofUrl);
    }
  }, [proofUrl, urlDirty]);

  const addReportFiles = (incoming: File[]) => {
    const result = validateAttachments(reportFiles, incoming);
    if (result.error) {
      setAttachmentError(result.error.message);
      return;
    }
    setAttachmentError(undefined);
    setReportFiles((prev) => [...prev, ...result.accepted]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) {
      addReportFiles(selected);
    }
    e.target.value = '';
  };

  const handleDeleteAttachment = (attachment: AttachmentInfo) => {
    setReportFiles((prev) =>
      prev.filter((f) => f.name !== attachment.attachmentId)
    );
    setAttachmentError(undefined);
  };

  // The bulk endpoint requires broadcast permission (Listing_BroadcastUnbroadcast),
  // whereas the single-listing apply only needs Inventory_UpdateListingInfo — gate
  // the bulk option separately so a user without broadcast rights isn't offered a
  // bulk apply that would 403.
  const hasBroadcastPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_BroadcastUnbroadcast,
    Permission.Inventory_BroadcastUnbroadcastOnPurchasedBy,
    Permission.Inventory_BroadcastUnbroadcastOnIsPricerOf
  );

  // Only surface the bulk option when the user can broadcast and there is more
  // than the source listing.
  const canBulkApply = hasBroadcastPermission && (bulk.affectedCount ?? 0) > 1;
  const isBusy = isApplying || bulk.isApplying;

  // Bulk apply runs through the SignalR bulk-edit hub so large operations show
  // real progress; it falls back to a synchronous apply (with a spinner) when
  // the hub isn't connected.
  const [bulkSubmitted, setBulkSubmitted] = useState(false);
  const updateKey = `${SECTION_MAPPING_BULK_UPDATE_KEY}-${listing.id}`;
  const { setMainDialogOpened, setProgress, setPreview, setStage } =
    useBulkEditHubContext();

  const closeAll = useCallback(() => {
    setPreviewOpen(false);
    setOpen(false);
    setSelectedSectionId(undefined);
    setApplyToAll(false);
    setPersistOverride(false);
    setBulkSubmitted(false);
    setReportMode(false);
    setReportText('');
    setReportFiles([]);
    setAttachmentError(undefined);
    setReportUrl('');
    setUrlDirty(false);
    setMainDialogOpened(false);
    setProgress(undefined);
    setPreview(undefined);
    setStage(BulkEditStage.Idle);
  }, [setMainDialogOpened, setPreview, setProgress, setStage]);

  const bulkRefresh = bulk.refresh;
  const onBulkEditDone = useCallback(async () => {
    await bulkRefresh();
    closeAll();
  }, [bulkRefresh, closeAll]);

  const { bulkEditHub, initJob, progress } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateMarketplaceSeating,
    updateKey,
    onBulkEditDone
  );
  const supportsBackground =
    bulkEditHub?.state === HubConnectionState.Connected;
  const isBulkRunning = bulk.isApplying || bulkSubmitted || progress != null;

  // Applying to all first opens a preview of the affected listings; a single
  // listing applies immediately.
  const onApply = async () => {
    if (!selectedSection || isBusy) {
      return;
    }
    if (applyToAll && canBulkApply) {
      setPreviewOpen(true);
      return;
    }
    if (await applyMapping(selectedSection, persistOverride)) {
      closeAll();
    }
  };

  // FR1: report the listing's section as missing. On success the surrounding
  // SectionMappingControl re-renders to the "requested" indicator (FR2/FR7).
  const onReport = async () => {
    if (isReporting) {
      return;
    }
    if (await reportMissingSection(reportText, reportFiles, reportUrl)) {
      closeAll();
    }
  };

  const onConfirmBulk = async () => {
    if (!selectedSection || isBulkRunning) {
      return;
    }
    setBulkSubmitted(true);
    const ok = await bulk.bulkApply(
      selectedSection,
      updateKey,
      supportsBackground,
      // Remap each listing's row into the new section (match, else speculative).
      true
    );
    if (!ok) {
      // Allow a retry if the request failed outright.
      setBulkSubmitted(false);
      return;
    }
    // Synchronous mode finishes here; background mode closes when the hub
    // reports completion (onBulkEditDone).
    if (!supportsBackground) {
      closeAll();
    }
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Tooltip
          contentVariant="dark"
          triggerContent={
            <Popover.Trigger asChild>
              <button
                type="button"
                className={styles.triggerButton}
                aria-label={triggerLabel}
              >
                {isReported ? (
                  <ClockIcon
                    fill={IconsFill.textBrand}
                    size={vars.iconSize.m}
                    noOutline
                  />
                ) : (
                  <WarningOutlineIcon
                    fill={IconsFill.textWarning}
                    size={vars.iconSize.m}
                    withHoverEffect
                  />
                )}
              </button>
            </Popover.Trigger>
          }
        >
          <Content
            id={
              isReported
                ? ContentId.SectionRequestPending
                : ContentId.SeatingInfoNotMatchStubHub
            }
          />
        </Tooltip>
        <Popover.Content align="end" className={styles.panel}>
          {reportMode ? (
            <Stack direction="column" gap="m">
              <span className={shared.typography.title6}>
                <Content id={ContentId.ReportMissingSection} />
              </span>
              <PosTextField
                value={reportUrl}
                disabled={isReporting}
                placeholder={proofUrlPlaceholder}
                maxLength={2048}
                onChange={(e) => {
                  setReportUrl(e.target.value);
                  setUrlDirty(true);
                }}
              />
              <PosTextArea
                value={reportText}
                disabled={isReporting}
                placeholder={reportPlaceholder}
                maxLength={1000}
                rows={4}
                onChange={(e) => setReportText(e.target.value)}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />
              <button
                type="button"
                className={styles.browseFilesButton}
                disabled={isReporting}
                onClick={() => fileInputRef.current?.click()}
              >
                <AddFileIcon size="16px" />
                <Content id={ContentId.BrowseFiles} />
              </button>
              {reportFiles.length > 0 && (
                <Stack direction="row" gap="s" flexWrap="wrap">
                  {reportFiles.map((f) => (
                    <AttachmentDisplay
                      key={f.name}
                      attachmentInfo={toAttachmentInfo(f)}
                      showDelete
                      onDeleteClicked={handleDeleteAttachment}
                    />
                  ))}
                </Stack>
              )}
              {attachmentError && (
                <span className={styles.attachmentError}>
                  {attachmentError}
                </span>
              )}
              <Stack direction="row" gap="m" justifyContent="end">
                <Button
                  variant="outline"
                  disabled={isReporting}
                  onClick={() => setReportMode(false)}
                >
                  <Content id={ContentId.Cancel} />
                </Button>
                <Button
                  variant="regular"
                  disabled={isReporting}
                  onClick={onReport}
                >
                  {isReporting ? (
                    <LoadingIcon
                      className={styles.spinner}
                      size={vars.iconSize.m}
                    />
                  ) : (
                    <Content id={ContentId.Submit} />
                  )}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack direction="column" gap="m">
              <span className={shared.typography.title6}>
                <Content id={ContentId.ChooseSection} />
              </span>
              {hasNoSections ? (
                <span className={styles.emptyMessage}>
                  <Content id={ContentId.SeatingInfoNotMatchStubHub} />
                </span>
              ) : (
                <SectionSelector
                  showFullWidth
                  sections={sections}
                  value={selectedSectionId ?? '-1'}
                  placeholderText={ContentId.SearchOrEnter}
                  loading={venueMapQuery.isLoading}
                  onChange={(value) => setSelectedSectionId(value)}
                />
              )}
              <Checkbox
                checked={persistOverride}
                disabled={isBusy}
                labelPosition="right"
                isV2Variant
                onChange={(e) => setPersistOverride(e.target.checked)}
                label={
                  <span>
                    <Content
                      id={ContentId.SectionMappingPersistForFutureInventory}
                    />
                  </span>
                }
              />
              {canBulkApply && (
                <Checkbox
                  checked={applyToAll}
                  disabled={isBusy}
                  labelPosition="right"
                  isV2Variant
                  onChange={(e) => setApplyToAll(e.target.checked)}
                  label={
                    <span>
                      <Content
                        id={ContentId.SectionMappingApplyToVenueConfig}
                      />{' '}
                      ({bulk.affectedCount})
                    </span>
                  }
                />
              )}
              {!isReported && (
                <button
                  type="button"
                  className={styles.reportLink}
                  onClick={() => setReportMode(true)}
                >
                  <Content id={ContentId.ReportMissingSectionPrompt} />{' '}
                  <Content id={ContentId.ReportMissingSection} />
                </button>
              )}
              <Stack direction="row" gap="m" justifyContent="end">
                <Popover.Close asChild>
                  <Button variant="outline">
                    <Content id={ContentId.Cancel} />
                  </Button>
                </Popover.Close>
                <Button
                  variant="regular"
                  disabled={!selectedSectionId || isBusy}
                  onClick={onApply}
                >
                  {isApplying ? (
                    <LoadingIcon
                      className={styles.spinner}
                      size={vars.iconSize.m}
                    />
                  ) : (
                    <Content id={ContentId.Apply} />
                  )}
                </Button>
              </Stack>
            </Stack>
          )}
        </Popover.Content>
      </Popover.Root>
      {previewOpen && (
        <GenericDialog
          isOpen={previewOpen}
          size="xl"
          centered
          onOpened={initJob}
          onCancel={isBulkRunning ? undefined : closeAll}
          header={<Content id={ContentId.Preview} />}
          footer={
            <Stack direction="row" gap="m" justifyContent="end">
              <Button disabled={isBulkRunning} onClick={closeAll}>
                <Content id={ContentId.Cancel} />
              </Button>
              <Button
                variant="regular"
                disabled={!selectedSection || isBulkRunning}
                onClick={onConfirmBulk}
              >
                {isBulkRunning ? (
                  <LoadingIcon
                    className={styles.spinner}
                    size={vars.iconSize.m}
                  />
                ) : (
                  <>
                    <Content id={ContentId.Confirm} /> ({bulk.affectedCount})
                  </>
                )}
              </Button>
            </Stack>
          }
        >
          <div className={styles.previewDialogBody}>
            <BulkEditStatus
              entityType={ActionOutboxEntityType.Listing}
              updateKey={updateKey}
              isLoading={bulk.isApplying}
            >
              <BulkEditPreviewListingDetails preview={bulk.preview} />
            </BulkEditStatus>
          </div>
        </GenericDialog>
      )}
    </>
  );
};
